import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../../../components/StatusBadge";
import { TextInput } from "../../../../components/TextInput";
import { Select, SelectOption } from "../../../../components/Select";
import { EditNoteIcon, CheckIcon, CrossIcon } from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import { useInlineEditor } from "../../../../hooks/useInlineEditor";
import {
  HeaderContainer,
  TitleWrap,
  TitleRow,
  PageTitle,
  TitleEditRow,
  TitleInputWrap,
  MetaRow,
  MetaContainer,
  MetaItem,
  HeaderActions,
  EditIconButton,
  SaveIconButton,
  CancelIconButton,
  StatusSelectWrap,
} from "./TreatmentPlanHeader.styled";

interface MetaItemData {
  text: string;
  key: string;
}

interface HeaderDraft {
  name: string;
  status: number;
}

interface TreatmentPlanHeaderProps {
  title: string;
  status: number;
  metaItems: MetaItemData[];
  actions?: ReactNode;
  isEditable?: boolean;
  canEditStatus?: boolean;
  statusOptions?: SelectOption[];
  getStatusLabel: (status: number) => string;
  getStatusType: (status: number) => string;
  onSave: (data: { name: string; status: number }) => Promise<void>;
  onEditingChange?: (isEditing: boolean) => void;
}

const TreatmentPlanHeader: React.FC<TreatmentPlanHeaderProps> = ({
  title,
  status,
  metaItems,
  actions,
  isEditable = false,
  canEditStatus = false,
  statusOptions = [],
  getStatusLabel,
  getStatusType,
  onSave,
  onEditingChange,
}) => {
  const { t } = useTranslation();
  const headerValue = useMemo(
    () => ({ name: title, status }),
    [title, status],
  );

  const validateName = useCallback(
    (name: string) => {
      if (!name.trim()) {
        return t("editTreatmentPlan.validation.nameRequired");
      }
      if (name.length > 50) {
        return t("editTreatmentPlan.validation.nameTooLong");
      }
      return undefined;
    },
    [t],
  );

  const handleSave = useCallback(
    async (draft: HeaderDraft) => {
      await onSave({ name: draft.name.trim(), status: draft.status });
    },
    [onSave],
  );

  const editorOptions = useMemo(
    () => ({
      validate: (draft: HeaderDraft) => validateName(draft.name),
    }),
    [validateName],
  );

  const {
    isEditing,
    draft,
    setDraft,
    saving,
    error: nameError,
    setError: setNameError,
    startEdit,
    cancelEdit,
    save,
  } = useInlineEditor(headerValue, handleSave, editorOptions);

  useEffect(() => {
    onEditingChange?.(isEditing);

    return () => {
      onEditingChange?.(false);
    };
  }, [isEditing, onEditingChange]);

  const renderStatus = () => {
    if (isEditing && canEditStatus) {
      return (
        <StatusSelectWrap>
          <Select
            name="treatment-plan-status"
            value={draft.status}
            size="small"
            onChange={(value: string | number | (string | number)[]) =>
              setDraft((current) => ({
                ...current,
                status: value as number,
              }))
            }
            options={statusOptions}
            placeholder={getStatusLabel(status)}
          />
        </StatusSelectWrap>
      );
    }

    return (
      <StatusBadge
        status={getStatusType(status)}
        label={getStatusLabel(status)}
      />
    );
  };

  return (
    <HeaderContainer>
      <TitleWrap>
        {isEditing ? (
          <TitleEditRow>
            <TitleInputWrap>
              <TextInput
                name="treatment-plan-name"
                value={draft.name}
                onChange={(event) => {
                  const nextName = event.target.value;
                  setDraft((current) => ({ ...current, name: nextName }));
                  if (nameError) {
                    setNameError(validateName(nextName));
                  }
                }}
                error={!!nameError}
                helperText={nameError}
                maxLength={50}
              />
            </TitleInputWrap>
            <SaveIconButton
              onClick={save}
              disabled={saving}
              aria-label={t("treatmentPlan.saveChanges")}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : (
                <CheckIcon
                  width="24px"
                  height="24px"
                  fill={colors.primary.main}
                />
              )}
            </SaveIconButton>
            <CancelIconButton
              onClick={cancelEdit}
              disabled={saving}
              aria-label={t("treatmentPlan.cancel")}
            >
              <CrossIcon
                width="24px"
                height="24px"
                fill={colors.status.error}
              />
            </CancelIconButton>
          </TitleEditRow>
        ) : (
          <TitleRow>
            <PageTitle>{title}</PageTitle>
            {isEditable && (
              <EditIconButton
                onClick={startEdit}
                aria-label={t("treatmentPlan.editTreatmentPlan")}
              >
                <EditNoteIcon width="20px" height="20px" />
              </EditIconButton>
            )}
          </TitleRow>
        )}

        {metaItems.length > 0 && (
          <MetaRow>
            <MetaContainer>
              {metaItems.map((item, index) => (
                <React.Fragment key={item.key}>
                  <MetaItem>{item.text}</MetaItem>
                  {index < metaItems.length - 1 && <MetaItem>|</MetaItem>}
                </React.Fragment>
              ))}
            </MetaContainer>
            {renderStatus()}
          </MetaRow>
        )}
      </TitleWrap>

      {actions && <HeaderActions>{actions}</HeaderActions>}
    </HeaderContainer>
  );
};

export default TreatmentPlanHeader;
